import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { pushTo } from '@app/core/routing';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { socialExportPublishMessages } from '@app/modules/social-export/publish';
import { fragmentsPermissions, webApi } from '@mi-front/api-clients';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface FragmentVideoEditionPublishMenuProps {
  /** Video editor edit id passed to the upload route for source preselection (`editId` query param). */
  editId: string;
  fragmentId: string;
  /** Fragment social uploads grouped by platform; drives which platforms still allow a new publish. */
  uploadsByPlatform: Map<string, webApi.FragmentSocialUploadItemDto[]> | undefined;
  /**
   * When true, only a share icon opens the menu (used next to the “published on …” label on edit cards).
   */
  iconOnly?: boolean;
}

/**
 * "Publicar en" dropdown for a generated edit: lists platforms available for a new publish
 * (same rules as the publish tab) and navigates to the upload screen with this edit preselected.
 */
export function FragmentVideoEditionPublishMenu({
  editId,
  fragmentId,
  uploadsByPlatform,
  iconOnly = false,
}: FragmentVideoEditionPublishMenuProps) {
  const { $t } = useIntl();
  const fragmentQuery = useFragmentQuery(fragmentId);
  const fragment = fragmentQuery.data;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { platforms: availablePlatforms } =
    useAvailableSocialExportPlatformsForStation<webApi.FragmentSocialUploadItemDto>(
      fragment?.stationId,
      uploadsByPlatform,
    );

  if (!fragment) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    const platform = availablePlatforms.find(
      (p) => p.id.toUpperCase() === platformId.toUpperCase(),
    );
    if (platform) {
      pushTo(routeGenerator.upload(fragment, platform, editId));
    }
    handleClose();
  };

  return (
    <IfPermission permission={fragmentsPermissions.Fragments_SocialPublish}>
      {availablePlatforms.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          <FormattedMessage {...socialExportPublishMessages.noPlatformsToPublish} />
        </Typography>
      ) : (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {iconOnly ? (
            <IconButton
              size="small"
              color="primary"
              onClick={handleClick}
              title={$t(socialExportPublishMessages.publishTo)}
              sx={{ px: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShareIcon fontSize="small" />
                <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.25, mr: -0.25 }} />
              </Box>
            </IconButton>
          ) : (
            <Button
              size="small"
              onClick={handleClick}
              startIcon={<ShareIcon />}
              endIcon={<ArrowDropDownIcon />}
            >
              {$t(socialExportPublishMessages.publishTo)}
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {availablePlatforms.map((platform) => (
              <MenuItem key={platform.id} onClick={() => handlePlatformSelect(platform.id)}>
                {platform.Icon({ sx: { mr: 1, fontSize: 20 }, colored: true })} {platform.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}
    </IfPermission>
  );
}
